import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, interval, takeUntil } from 'rxjs';
import { BasicMachineFunctionsService } from 'src/app/services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { DidConfigService, t_Did } from 'src/app/services/apiHandler/did-config/did-config.service';
import { DidDataRetrievalService, t_DataIdentifier } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { GlobalService } from 'src/app/services/helper/global-helper';
import { RoleService } from 'src/app/services/helper/role-handling-helper';

type t_machineOptions = {
  optionName: string,
  optionDescription: string,
  inputOptions: {
    type: string,
    choices?: number[],
    range?: number[],
    step?: number,
    custom?: Record<string, string>
  },  
  choiceOptionDataName: string,
  choiceStateDataName: string
}

type t_tableData = {
  optionName: string,
  optionDescription: string,  
  type: string,
  choices?: number[],
  range?: number[],
  step?: number,
  custom?: { key: string; label: string}[],
  chosenValue?: number,
  choiceOptionDataName: string,
  choiceStateDataName: string,
  loading?: boolean,
  error?: boolean
}


@Component({
  selector: 'app-machine-options',
  templateUrl: './machine-options.component.html',
  styleUrls: ['./machine-options.component.scss']
})
export class MachineOptionsComponent implements OnInit, OnDestroy {

  public loaded: boolean = false;
  public displayColumns: string[] = []; //["name", "read_value", "write_value", "button"];
  public dataSet: MatTableDataSource<t_tableData> = new MatTableDataSource<t_tableData>;
  public machineOptions:  t_machineOptions[] = [];
  public interval: Subscription | undefined;
  private destroyed: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();
  private line: string = '';
  public nonFound: boolean = false;
  public noMachineOptions: boolean = false;
  public exportLoad: boolean = false;

  public version = '';
  public tabArray: string[] = [];
  private machineOptionsFile!: { type: string; version: string; optionsGroup: [{optionGroupName: string, optionGroupOptions: t_machineOptions[]}]; };
  public tabIndex: number = 0;
  public tabLoaded: boolean = false;

  public numeric = [0,1];

  // There could be Problems if the dataids are also used elsewhere(for testpurpose machinetime and resets used - problem arised when went to operationtime overview)
  // But probably not important as machineOptions are not used anywhere else
  // KPS to be determined - further testing necessary with actual machineOptions

  constructor(public configService: DidConfigService, public retrievalService: DidDataRetrievalService,public gatewayConnectionService: GatewayConnectionService, private translate: TranslateService, public roleService: RoleService, private global: GlobalService, private machineService: BasicMachineFunctionsService){
    //this.machineOptions = this.machineOptionsConfig.machineOptions;
    this.global.machineInfos.subscribe({
      next: newValue => {this.line = newValue.line;}
    });

    if(this.roleService.isRole('MO_Write')) {
      this.displayColumns = ["name", "read_value", "write_value", "button"];
    } else if(this.roleService.isRole('MO_Read')) {
      this.displayColumns = ["name", "read_value"];
    }
  }

  ngOnInit() {
    this.setupServices().then(() => {
      if(!this.destroyed) {
        this.retrievalService.initService();
        this.getDataIdentifiers().then(() => {
          this.initValues().then(() => {
            if(this.machineOptions.length <= 0) {
              this.noMachineOptions = true;
            } else {
              this.tabLoaded = true;
            }
            this.loaded = true;
            this.interval = interval(2000).pipe(takeUntil(this.destroy$)).subscribe(() => {
              this.checkValue();
            });
          });
        });
      }
    });
  }

  ngOnDestroy(){
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.interval?.unsubscribe();
    this.retrievalService.stopSendQueryAliveState();
    this.retrievalService.stopDataQuery();
    this.gatewayConnectionService.checkGatewayAlive().subscribe({
      next: (isAlive) => {
          if(isAlive){
            this.machineOptions.forEach((option) => {
              let identifier = this.configService.getDataIdentifierOfDataName(option.choiceStateDataName);
                this.configService.removeDidFromQueryList(identifier).subscribe();
            }); 
          }
      }
    });
  }

  private setupServices(): Promise<void> {
    return new Promise((resolve) => {
      this.gatewayConnectionService.establishConnectionToGateway().then(
        () => {
          this.machineService.getMachineOptions(this.line).subscribe({
            next: (resp) => {
              let file = JSON.parse(JSON.stringify(resp.body));
              this.handleVersion(file);
              if(this.machineOptions.length === 0 && this.version != '2.0') {
                this.nonFound = true;
                this.loaded = true;
              }
              resolve();
            },
            error: () => {
              this.nonFound = true;
              this.loaded = true;
              //resolve();
            }
          });
        },
        () => {
          console.error('ERROR');
        }
      )
    });
  }

  private handleVersion(file: any) {
    if(file.version && file.type) {
      this.version = file.version;
      this.machineOptionsFile = file;
      this.machineOptionsFile.optionsGroup.forEach((element) => {
        this.tabArray.push(element.optionGroupName);
      });
      this.machineOptions = file.optionsGroup[0].optionGroupOptions;      
    } else {
      this.machineOptions = file;
    }
  }

  // checks Value in interval of 2s if there are any changes
  public checkValue() {
    if(this.loaded) {
      for(let i = 0; i < this.dataSet.data.length; i++){
        let newChoiceStateDataName = this.dataSet.data[i].choiceStateDataName;
        let index = this.machineOptions.findIndex((opt) => {
          return opt.choiceOptionDataName == this.dataSet.data[i].choiceOptionDataName;
        });
        let identifier = this.configService.getDataIdentifierOfDataName(this.machineOptions[index].choiceStateDataName);
        let information = this.configService.getDidInformation(identifier);
        if(identifier.ecu_id != undefined) {
          this.retrievalService.getDidsDetails([identifier]).subscribe({
            next: (response) => {
              if(response.body != null) {
                let value = response.body;
                if(this.machineOptions[i].inputOptions.type == 'select') {  
                  if(this.isBinar(this.machineOptions[i].inputOptions.choices!)) {
                    if(value[0].data![value[0].data!.length-1].value[0] == 0){
                      newChoiceStateDataName = this.translate.instant("OPTIONS.DEACTIVATED");
                    } else if(value[0].data![value[0].data!.length-1].value[0] == 1) {
                      newChoiceStateDataName = this.translate.instant("OPTIONS.ACTIVATED");
                    }                
                  } else {
                    let newValue = this.normalizedValue(value[0].data![value[0].data!.length-1].value[0], information!.conversionFactor, information!.conversionOffset);
                    newChoiceStateDataName = newValue.toString();
                  }
                } else if(this.machineOptions[i].inputOptions.type == 'range') {
                  let newValue = this.normalizedValue(value[0].data![value[0].data!.length-1].value[0], information!.conversionFactor, information!.conversionOffset);
                  newChoiceStateDataName = newValue.toString();
                } else if(this.machineOptions[i].inputOptions.type == 'custom') {
                  let val = this.machineOptions[i].inputOptions.custom![value[0].data![value[0].data!.length-1].value[0].toString()];
                  newChoiceStateDataName = val;
                }
              }
              this.dataSet.data[i].choiceStateDataName = newChoiceStateDataName;
            }
          });
        }
      }
    }
  }
  
  private normalizedValue(value: number, factor: number, offset: number) {
    if(factor == 0) {
      factor = 1;
    }
    let res: number = (value * factor) + offset;
    res = parseFloat(res.toFixed(1));
    return res;
  }

  public getDataIdentifiers(): Promise<void> {
    return new Promise((resolve) => {
      if(this.destroyed){
        this.ngOnDestroy();
      }
      let count = 0;
      this.machineOptions = this.machineOptions.filter((option) =>
        this.configService.getDataIdentifierOfDataName(option.choiceStateDataName).ecu_id !== undefined);
      if(this.machineOptions.length <= 0) {
        resolve();
      }
      this.machineOptions.forEach((option) => {
        let identifier = this.configService.getDataIdentifierOfDataName(option.choiceStateDataName);
        this.configService.addDidToQueryList(identifier).subscribe();
        count++;
        if(count === this.machineOptions.length){
          this.retrievalService.startDataQuery();
          resolve();
        }
      });
    });
  }

  public initValues(): Promise<void> {
    return new Promise(async (resolve) => {
      if(this.destroyed){
        this.ngOnDestroy();
      }
      let count = 0;
      if(this.machineOptions.length <= 0) {
        resolve();
      }
      for( const option of this.machineOptions) {
        let identifier = this.configService.getDataIdentifierOfDataName(option.choiceStateDataName);
        let information = this.configService.getDidInformation(identifier);
        await this.getData(option, identifier, information!);
        count++;
        if(count === this.machineOptions.length){
          resolve();
        }
      }
    });
  }

  public getData(option: t_machineOptions, identifier: t_DataIdentifier, information: t_Did): Promise<void> {
    return new Promise((resolve) => {
      this.retrievalService.getDidsDetails([identifier]).subscribe({
        next: (response) => {
          if(response.body != null) {
            let value = response.body;
            let choiceStateDataName = "";
            let customArray: string[] = [];
            if(value[0].data!.length > 0) {
              if(option.inputOptions.type == 'select') {
                if(this.isBinar(option.inputOptions.choices!)) {
                  if(value[0].data![value[0].data!.length-1].value[0] == 0){
                  choiceStateDataName = this.translate.instant("OPTIONS.DEACTIVATED");
                  } else if(value[0].data![value[0].data!.length-1].value[0] == 1) {
                    choiceStateDataName = this.translate.instant("OPTIONS.ACTIVATED");
                  }
                } else {
                  let newValue = this.normalizedValue(value[0].data![value[0].data!.length-1].value[0], information!.conversionFactor, information!.conversionOffset);
                  choiceStateDataName = newValue.toString();
                }
              } else if(option.inputOptions.type == 'range') {
                let newValue = this.normalizedValue(value[0].data![value[0].data!.length-1].value[0], information!.conversionFactor, information!.conversionOffset);
                choiceStateDataName = newValue.toString();
              } else if(option.inputOptions.type == 'custom') {
                let val = option.inputOptions.custom![value[0].data![value[0].data!.length-1].value[0].toString()];
                choiceStateDataName = val;
              }
            } else {
              choiceStateDataName = this.translate.instant("OPTIONS.LOADING");
            }
            if(option.inputOptions.type == 'select'){
              let row: t_tableData = {
                optionName: option.optionName,
                optionDescription: option.optionDescription,
                type: option.inputOptions.type,
                choices: option.inputOptions.choices!,
                choiceOptionDataName: option.choiceOptionDataName,
                choiceStateDataName: choiceStateDataName
              };
              this.dataSet.data.push(row);
              resolve();
            } else if(option.inputOptions.type == 'range') {
              let row: t_tableData = {
                optionName: option.optionName,
                optionDescription: option.optionDescription,
                type: option.inputOptions.type,
                range: option.inputOptions.range,
                step: option.inputOptions.step,
                choiceOptionDataName: option.choiceOptionDataName,
                choiceStateDataName: choiceStateDataName
              };
              this.dataSet.data.push(row);
              resolve();
            } else if(option.inputOptions.type == 'custom') {
              let row: t_tableData = {
                optionName: option.optionName,
                optionDescription: option.optionDescription,
                type: option.inputOptions.type,
                custom: Object.entries(option.inputOptions.custom!).map(([key, label]) => ({
                  key, label
                })),
                choiceOptionDataName: option.choiceOptionDataName,
                choiceStateDataName: choiceStateDataName
              };
              this.dataSet.data.push(row);
              resolve();
            }
          }
        }
      });
    });
    
  }

  public isInRange(value: number | null, range: [number, number]): boolean {
    if(value == null) {
      return false;
    } 
    return value >= range[0] && value <= range[1];
  }

  public changeValue(element: t_tableData){
   element.loading = true;
    if(element.chosenValue != undefined) {
      let identifier = this.configService.getDataIdentifierOfDataName(element.choiceOptionDataName);
      let information = this.configService.getDidInformation(identifier);
      let newValue = this.denormalizedValue(Number(element.chosenValue), information!.conversionFactor, information!.conversionOffset);
      this.configService.writeDataByID(identifier.ecu_id, identifier.data_id, newValue).subscribe({
        error: (error) => {
          console.error(error);
          element.loading = false;
          element.error = true;
          element.chosenValue = undefined;
          setTimeout(() => element.error = false, 2000);
        },
        complete() {
          element.loading = false;
          //element.chosenValue = undefined;
        },
      });
    } 
  }

  private denormalizedValue(value: number, factor: number, offset: number) {
    if(factor == 0) {
      factor = 1;
    }
    let res: number = (value / factor) - offset;
    res = Math.round(res);
    return res;
  }

  change($event: any) {
    this.tabIndex = $event.index;
    this.fillTable();
  }

  public async exportToCSV() {
    this.exportLoad = true;
    this.tabLoaded = false;
    if(this.machineOptionsFile) {
      let csvRows: string[] = [];
      csvRows.push('optionGroup;optionName;currentValue');
      for(let group of this.machineOptionsFile.optionsGroup) {

        let data = await this.getGroupData(group);      
        // when group is empty
        if(data.length <= 0) {
          csvRows.push([
            this.escapeCsvValue(group.optionGroupName),
            this.escapeCsvValue('-'),
            this.escapeCsvValue('-')
          ].join(';'));
        }

        for(let item of data) {
          csvRows.push([
            this.escapeCsvValue(group.optionGroupName),
            this.escapeCsvValue(item.optionName),
            this.escapeCsvValue(item.currentValue)
          ].join(';'));
        }
      }

      this.fillTable();
      this.downloadCsv(csvRows.join('\r\n'));
    } else {
      let csvData = this.dataSet.data.map(item => ({optionName: item.optionName, currentValue: item.choiceStateDataName}));
      let headers = ['optionName', 'currentValue'];

      let csvRows = [
        headers.join(';'),
        ...csvData.map(item => 
          [
            this.escapeCsvValue(item.optionName),
            this.escapeCsvValue(item.currentValue)
          ].join(';')
        )
      ];

      this.downloadCsv(csvRows.join('\r\n'));
    }  
    this.exportLoad = false;
  }

  private getGroupData(group: any): Promise<{optionName: string, currentValue: string}[]> {
    return new Promise((resolve) => {
      this.machineOptions = group.optionGroupOptions;
      if(this.machineOptions.length > 0) {
        let newData: t_tableData[] = []
        this.dataSet.data = newData;
        this.getDataIdentifiers().then(() => {
          this.initValues().then(() => {
            let csvData = this.dataSet.data.map(item => ({optionName: item.optionName, currentValue: item.choiceStateDataName}));
            resolve(csvData);
          });
        });
      } else {
        resolve([]);
      }
    });
  }

  private fillTable() {
    this.machineOptions = this.machineOptionsFile.optionsGroup[this.tabIndex].optionGroupOptions;
    this.noMachineOptions = false;
    this.interval?.unsubscribe();
    this.tabLoaded = false;
    let newData: t_tableData[] = []
    this.dataSet.data = newData;
    if(this.machineOptions.length > 0) {
      this.getDataIdentifiers().then(() => {
        this.initValues().then(() => {
          if(this.machineOptions.length <= 0) {
            this.noMachineOptions = true;
          } else {
            this.tabLoaded = true;
          }
          this.interval = interval(2000).pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.checkValue();
          });
        });
      });
    } else {
      this.noMachineOptions = true;
    }
  }

  private downloadCsv(csv: string) {
    let url: string | undefined;

    try {
      let blob = new Blob(['\uFEFF' + csv], {
        type: 'text/csv;charset=utf-8;'
      });

      url = window.URL.createObjectURL(blob);

      let link = document.createElement('a');
      link.href = url;
      link.download = 'machineOptions.csv';
      link.click();
    } catch (error) {
      console.error(error);
      throw new Error('Could not download CSV file');
    } finally {
      if(url) {
        window.URL.revokeObjectURL(url);
      }
    } 
  }

  private escapeCsvValue(value: string): string {
    if(value == null) {
      return '';
    }
    return `"${String(value).replace(/"/g, '""')}"`;
  }

  public isBinar(el: number[]): boolean {
    return el.length === this.numeric.length && this.numeric.every(x => el.includes(x));
  }

  public isValidValue(element:any): boolean {
    // check if empty
    let inputValue = element.chosenValue;
    if(inputValue === null || inputValue === undefined || inputValue === '') {
      return false;
    }

    let value = Number(element.chosenValue);
    if(!Number.isFinite(value)) {
      return false;
    }

    let [min,max] = element.range;
    //Range validation
    if(value < min || value > max) {
      return false;
    }

    //Step validation
    let step = 1;
    if(element.step != undefined) {
      step = Number(element.step);
    }

    if(!Number.isFinite(step) || step <= 0) {
      return false;
    }

    // Step is relative to min
    let steps = (value - min) / step;

    return Math.abs(steps - Math.round(steps)) < 1e-10;
  }

}
